import { first } from 'lodash';

import { SearchInput } from '@/components/form/search-input';
import { isRecordsLikeCell } from '@/core/cell';
import { getPipelineByIdOrThrow } from '@/core/exploration';

import { Exploration, Fields, Metric, Model } from '../types';
import { getPipelineFields } from '../pipeline/state';
import { getExplorationVariables } from '../utils';
import { createFieldOption } from '../components/select-options/field';
import { useEnsureFieldsExist } from './hooks/use-ensure-fields-exist';
import { useExplorationUIContext } from '../exploration/exploration-ui-context';
import {
  constructRelationOptionValue,
  deconstructRelationOptionValue,
} from '../components/select-options/utils';
import { createJoinableCellOptions } from '../components/select-options/cell';

import form from '../../components/form/form.module.scss';

type Data = {
  pipelineId: string;
  joinStrategy: {
    joinKeyOnBase: string;
    joinKeyOnRelated: string;
  };
};

type EditJoinStrategyFieldsProps = {
  data: Data;
  exploration: Exploration;
  models: Model[];
  metrics: Metric[];
  fields: Fields;
  onChange: (data: Data) => void;
};

export const EditJoinStrategyFields = (props: EditJoinStrategyFieldsProps) => {
  const { data, onChange, exploration, models, metrics, fields } = props;
  const { selectedCell } = useExplorationUIContext();

  if (selectedCell === null || !isRecordsLikeCell(selectedCell)) {
    throw new Error(`EditJoinStrategyFields can only be rendered in a RecordsCell`);
  }

  const pipelineOptions = createJoinableCellOptions(exploration, selectedCell);

  const explorationVariables = getExplorationVariables(exploration);
  const stateContext = {
    models,
    variables: explorationVariables,
    metrics,
    exploration,
  };
  const baseFields = useEnsureFieldsExist(fields, [data.joinStrategy.joinKeyOnBase]);

  const relatedFields = useEnsureFieldsExist(
    getPipelineFields(getPipelineByIdOrThrow(data.pipelineId, exploration), stateContext),
    [data.joinStrategy.joinKeyOnRelated],
  );

  const handleRelatedPipelineChange = (value: string) => {
    const reference = deconstructRelationOptionValue(value);
    if (!('pipelineId' in reference)) {
      throw new Error(`Invalid pipelineId in value ${value}`);
    }
    const { pipelineId } = reference;
    const firstRelatedField = first(
      getPipelineFields(getPipelineByIdOrThrow(pipelineId, exploration), stateContext),
    );

    if (firstRelatedField === undefined) {
      throw new Error(`Pipeline ${pipelineId} does not have any fields`);
    }

    onChange({
      ...data,
      pipelineId,
      joinStrategy: {
        ...data.joinStrategy,
        joinKeyOnRelated: firstRelatedField.key,
      },
    });
  };

  const handleBaseFieldChange = (key: string) => {
    onChange({
      ...data,
      joinStrategy: {
        ...data.joinStrategy,
        joinKeyOnBase: key,
      },
    });
  };

  const handleRelatedFieldChange = (key: string) => {
    onChange({
      ...data,
      joinStrategy: {
        ...data.joinStrategy,
        joinKeyOnRelated: key,
      },
    });
  };

  return (
    <>
      <div className={form.formRow}>
        <label className={form.formLabel}>Data</label>
        <SearchInput
          options={pipelineOptions}
          value={constructRelationOptionValue({ pipelineId: data.pipelineId })}
          onChange={handleRelatedPipelineChange}
        />
      </div>
      <div className={form.formRow}>
        <label className={form.formLabel}>Base column</label>
        <SearchInput
          options={baseFields.map(createFieldOption)}
          value={data.joinStrategy.joinKeyOnBase}
          onChange={handleBaseFieldChange}
        />
      </div>
      <div className={form.formRow}>
        <label className={form.formLabel}>Related column</label>
        <SearchInput
          options={relatedFields.map(createFieldOption)}
          value={data.joinStrategy.joinKeyOnRelated}
          onChange={handleRelatedFieldChange}
        />
      </div>
    </>
  );
};
